import { useState, useEffect } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Plus, ArrowLeft, Loader2, Sparkles, UtensilsCrossed, FileDown, Euro, Wine, LayoutGrid, List, Search,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatCurrency } from "../lib/utils";
import CostSimulator from "../components/menus/CostSimulator";
import DishPickerDialog from "../components/menus/DishPickerDialog";
import { CategoryDialog } from "../components/menus/CategoryDialog";
import { DishDialog } from "../components/menus/DishDialog";
import { CategorySection } from "../components/menus/CategorySection";
import BeverageSelector from "../components/menus/BeverageSelector";
import MenuTPVView from "../components/menus/MenuTPVView";
import StarterUsageBanner from "../components/menus/StarterUsageBanner";

const DAYS = [
  { key: "mon", label: "Lun" }, { key: "tue", label: "Mar" },
  { key: "wed", label: "Mie" }, { key: "thu", label: "Jue" },
  { key: "fri", label: "Vie" }, { key: "sat", label: "Sab" },
  { key: "sun", label: "Dom" },
];

export default function MenuDetailPage() {
  const { menuId } = useParams();
  const { currentRestaurant, sidebarCollapsed } = useOutletContext();
  const navigate = useNavigate();

  const [menu, setMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Restaurant dishes for the recipe selector
  const [restaurantDishes, setRestaurantDishes] = useState([]);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [dishDialogOpen, setDishDialogOpen] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [menuPrice, setMenuPrice] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  const [availableDays, setAvailableDays] = useState([]);

  // Daily menu linked items
  const [dailyItems, setDailyItems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCategoryId, setPickerCategoryId] = useState("");
  const [pickerCategoryName, setPickerCategoryName] = useState("");
  const [beverageSelectorOpen, setBeverageSelectorOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" | "tpv"
  const [searchQuery, setSearchQuery] = useState("");

  // In TPV mode, prevent parent scroll
  useEffect(() => {
    if (viewMode === "tpv") {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [viewMode]);

  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
  const [dishForm, setDishForm] = useState({
    name: "", description: "", price: "", category_id: "",
    is_active: true, allergens: [], tags: [],
  });

  useEffect(() => {
    if (!currentRestaurant) return;
    fetchData();
  }, [menuId, currentRestaurant]); // eslint-disable-line

  useEffect(() => {
    if (!currentRestaurant) return;
    api.getAllDishesForRestaurant(currentRestaurant.restaurant_id)
      .then(setRestaurantDishes).catch(() => setRestaurantDishes([]));
  }, [currentRestaurant]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [menusAll, categoriesData, dishesData] = await Promise.all([
        api.getMenus(currentRestaurant?.restaurant_id).catch(() => []),
        api.getCategories(menuId),
        api.getDishes(menuId),
      ]);
      const foundMenu = menusAll.find(m => m.menu_id === menuId);
      setMenu(foundMenu || null);
      if (foundMenu?.price) setMenuPrice(foundMenu.price.toString());
      if (foundMenu?.available_days) setAvailableDays(foundMenu.available_days);
      setCategories(categoriesData);
      if (foundMenu?.menu_type === "daily") {
        try { const d = await api.getDailyMenuItems(menuId); setDailyItems(d.items || []); } catch { /* ignore */ }
      }
      setDishes(dishesData);
    } catch (error) {
      console.error("Failed to fetch menu data:", error);
      toast.error("Error al cargar el menu");
    } finally { setLoading(false); }
  };

  // ─── Category handlers ────────────────────────────────────────────

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      const nc = await api.createCategory(menuId, { ...categoryForm, display_order: categories.length });
      setCategories([...categories, nc]);
      setCategoryDialogOpen(false);
      setCategoryForm({ name: "", description: "" });
      toast.success("Categoria creada");
    } catch (error) { toast.error(error.message || "Error al crear categoria"); }
    finally { setSaving(false); }
  };

  // ─── Dish handlers ────────────────────────────────────────────────

  const handleCreateOrUpdateDish = async (e) => {
    e.preventDefault();
    if (!dishForm.name.trim() || !dishForm.price || !dishForm.category_id) {
      toast.error("Nombre, precio y categoria son requeridos"); return;
    }
    setSaving(true);
    try {
      const dishData = { ...dishForm, price: parseFloat(dishForm.price) };
      if (editingDish) {
        const u = await api.updateDish(editingDish.dish_id, dishData);
        setDishes(dishes.map(d => d.dish_id === editingDish.dish_id ? u : d));
        toast.success("Plato actualizado");
      } else {
        const n = await api.createDish(menuId, dishData);
        setDishes([...dishes, n]); toast.success("Plato creado");
      }
      setDishDialogOpen(false); setEditingDish(null);
      setDishForm({ name: "", description: "", price: "", category_id: "", is_active: true, allergens: [], tags: [] });
    } catch (error) { toast.error(error.message || "Error al guardar plato"); }
    finally { setSaving(false); }
  };

  const handleDeleteDish = async (dishId) => {
    if (!window.confirm("¿Eliminar este plato?")) return;
    try { await api.deleteDish(dishId); setDishes(dishes.filter(d => d.dish_id !== dishId)); toast.success("Plato eliminado"); }
    catch { toast.error("Error al eliminar plato"); }
  };

  const openEditDish = (dish) => {
    setEditingDish(dish);
    setDishForm({ name: dish.name, description: dish.description || "", price: dish.price.toString(), category_id: dish.category_id, is_active: dish.is_active, allergens: dish.allergens || [], tags: dish.tags || [] });
    setDishDialogOpen(true);
  };

  // ─── Daily menu handlers ──────────────────────────────────────────

  const handleSaveMenuPrice = async () => {
    const priceNum = parseFloat(menuPrice);
    if (isNaN(priceNum) || priceNum < 0) { toast.error("Introduce un precio valido"); return; }
    setSavingPrice(true);
    try {
      const u = await api.updateMenu(menuId, { price: priceNum, available_days: availableDays });
      setMenu(u); toast.success("Menu del dia actualizado");
    } catch (err) { toast.error(err.message || "Error al guardar"); }
    finally { setSavingPrice(false); }
  };

  const toggleDay = (dayKey) => setAvailableDays(prev => prev.includes(dayKey) ? prev.filter(d => d !== dayKey) : [...prev, dayKey]);

  const openDishPicker = (categoryId, categoryName) => { setPickerCategoryId(categoryId); setPickerCategoryName(categoryName); setPickerOpen(true); };

  const reloadDailyItems = async () => { try { const d = await api.getDailyMenuItems(menuId); setDailyItems(d.items || []); } catch { /* ignore */ } };

  const handleRemoveDailyItem = async (dishId) => {
    try { await api.removeDailyMenuItem(menuId, dishId); setDailyItems(prev => prev.filter(i => i.dish_id !== dishId)); toast.success("Plato desvinculado"); }
    catch (err) { toast.error(err.message || "Error"); }
  };

  // ─── PDF Export ───────────────────────────────────────────────────

  const filteredDishes = searchQuery.trim()
    ? dishes.filter(d => d.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : dishes;

  const getDishesForCategory = (categoryId) => filteredDishes.filter(d => d.category_id === categoryId);

  const handleExportPDF = () => {
    const menuName = menu?.name || "Menu";
    const logoUrl = currentRestaurant?.logo_url;
    const printContent = `<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8" /><title>${menuName}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Georgia, "Times New Roman", serif; padding: 48px; max-width: 800px; margin: 0 auto; color: #1a1a1a; background: #fff; }
  .header { text-align: center; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #1a1a1a; }
  .header img { width: 80px; height: 80px; object-fit: cover; border-radius: 12px; margin-bottom: 12px; }
  h1 { font-size: 32px; font-weight: bold; letter-spacing: 1px; margin-bottom: 4px; }
  .subtitle { color: #666; font-size: 14px; }
  .category { margin-top: 28px; }
  .category-title { font-size: 13px; font-weight: bold; text-transform: uppercase; letter-spacing: 3px; color: #888; margin-bottom: 16px; padding-bottom: 4px; border-bottom: 1px solid #e0e0e0; }
  .dish { display: flex; gap: 16px; padding: 14px 0; border-bottom: 1px solid #f2f2f2; }
  .dish:last-child { border-bottom: none; }
  .dish-img { width: 80px; height: 64px; object-fit: cover; border-radius: 6px; }
  .dish-img-placeholder { width: 80px; height: 64px; background: #f5f5f5; border-radius: 6px; }
  .dish-info { flex: 1; }
  .dish-name { font-size: 16px; font-weight: bold; margin-bottom: 4px; }
  .dish-desc { color: #666; font-size: 13px; line-height: 1.5; margin-bottom: 6px; }
  .dish-price { font-size: 16px; font-weight: bold; color: #c0392b; }
  .footer { text-align: center; font-size: 11px; color: #aaa; margin-top: 40px; padding-top: 16px; border-top: 1px solid #e0e0e0; }
  @media print { body { padding: 24px; } }
</style></head><body>
  <div class="header">${logoUrl ? `<img src="${logoUrl}" alt="Logo" crossorigin="anonymous" /><br/>` : ""}<h1>${menuName}</h1>${menu?.description ? `<p class="subtitle">${menu.description}</p>` : ""}</div>
  ${categories.map(cat => {
    const catDishes = getDishesForCategory(cat.category_id);
    if (catDishes.length === 0) return "";
    return `<div class="category"><div class="category-title">${cat.name}</div>${catDishes.map(d => `<div class="dish">${d.image_url ? `<img class="dish-img" src="${d.image_url}" alt="${d.name}" crossorigin="anonymous" />` : `<div class="dish-img-placeholder"></div>`}<div class="dish-info"><div class="dish-name">${d.name}</div>${d.description ? `<div class="dish-desc">${d.description}</div>` : ""}<div class="dish-price">${formatCurrency(d.price)}</div></div></div>`).join("")}</div>`;
  }).join("")}
  <div class="footer">Generado con MenuGenius</div>
</body></html>`;
    const w = window.open("", "_blank", "width=850,height=1100");
    if (!w) { toast.error("Activa las ventanas emergentes para exportar el PDF"); return; }
    const doc = w.document;
    doc.open();
    doc.write(printContent);
    doc.close();
    w.onload = () => { w.focus(); w.print(); };
    toast.success("Abriendo vista de impresion...");
  };

  // ─── Render ───────────────────────────────────────────────────────

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className={viewMode === "tpv" ? `fixed inset-0 ${sidebarCollapsed ? "lg:left-[68px]" : "lg:left-64"} top-16 z-20 flex flex-col bg-background transition-[left] duration-200 ease-in-out` : "space-y-6"} data-testid="menu-detail-page">
      {/* Header */}
      <div className={viewMode === "tpv" ? "flex-shrink-0 px-4 lg:px-8 pt-4 pb-3 bg-background border-b border-border space-y-3" : ""}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/app/menus")}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className={`font-heading font-bold ${viewMode === "tpv" ? "text-lg" : "text-2xl"}`}>{menu?.name || "Detalle del Menu"}</h1>
              <p className="text-muted-foreground text-sm">{categories.length} categorias, {dishes.length} platos</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="flex bg-muted rounded-lg p-0.5" data-testid="view-mode-toggle">
              <button onClick={() => setViewMode("list")}
                className={`px-2.5 py-1.5 rounded-md transition-colors flex items-center gap-1.5 text-xs font-medium ${viewMode === "list" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="view-mode-list"><List className="w-3.5 h-3.5" /> Lista</button>
              <button onClick={() => setViewMode("tpv")}
                className={`px-2.5 py-1.5 rounded-md transition-colors flex items-center gap-1.5 text-xs font-medium ${viewMode === "tpv" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="view-mode-tpv"><LayoutGrid className="w-3.5 h-3.5" /> Visual</button>
            </div>
            <Button variant="outline" size={viewMode === "tpv" ? "sm" : "default"} onClick={() => setBeverageSelectorOpen(true)} data-testid="add-beverages-btn">
              <Wine className="w-4 h-4 mr-1" /> Bebidas
            </Button>
            <Button variant="outline" size={viewMode === "tpv" ? "sm" : "default"} onClick={handleExportPDF} data-testid="export-pdf-btn">
              <FileDown className="w-4 h-4 mr-1" /> PDF
            </Button>
            <CategoryDialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}
              form={categoryForm} setForm={setCategoryForm} onSubmit={handleCreateCategory} saving={saving} />
            <DishDialog open={dishDialogOpen} onOpenChange={setDishDialogOpen}
              editingDish={editingDish} form={dishForm} setForm={setDishForm}
              onSubmit={handleCreateOrUpdateDish} saving={saving}
              categories={categories} restaurantDishes={restaurantDishes} />
          </div>
        </div>

        {/* Search bar */}
        <div className="relative" data-testid="menu-search-bar">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar plato por nombre..."
            className="pl-9 max-w-md"
            data-testid="menu-search-input"
          />
        </div>
      </div>

      {/* Starter plan usage banner — shows live dish & credit usage with CTAs */}
      {viewMode !== "tpv" && (
        <StarterUsageBanner
          restaurantId={currentRestaurant?.restaurant_id}
          dishCount={dishes.length}
        />
      )}

      {/* Daily Menu Config */}
      {menu?.menu_type === "daily" && (
        <Card className="border-none shadow-soft" data-testid="daily-menu-config">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <UtensilsCrossed className="w-5 h-5 text-primary" /> Configuracion del Menu del Dia
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Euro className="w-4 h-4" /> Precio del menu</Label>
                <div className="flex gap-2">
                  <Input type="number" step="0.5" value={menuPrice} onChange={(e) => setMenuPrice(e.target.value)}
                    placeholder="12.50" className="max-w-[200px]" data-testid="daily-menu-price" />
                  <Button onClick={handleSaveMenuPrice} disabled={savingPrice} data-testid="save-daily-price-btn">
                    {savingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dias disponibles</Label>
                <div className="flex gap-2 flex-wrap">
                  {DAYS.map(day => (
                    <button key={day.key} onClick={() => toggleDay(day.key)} data-testid={`day-${day.key}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                        availableDays.includes(day.key)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-muted text-muted-foreground border-muted hover:bg-muted/80"
                      }`}>{day.label}</button>
                  ))}
                </div>
              </div>
            </div>
            {menu.price > 0 && (
              <CostSimulator
                menuId={menu.menu_id}
                menuPrice={menu.price}
                categories={categories}
                dishes={dishes.length > 0 ? dishes : dailyItems.map(di => ({
                  dish_id: di.dish_id,
                  name: di.dish_name || di.name || "Plato",
                  category_id: di.category_id || "",
                  cost: di.cost || 0,
                  price: di.price || 0,
                }))}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Categories & Dishes — List or TPV view */}
      {viewMode === "tpv" ? (
        <div className="flex-1 min-h-0">
          <MenuTPVView
            categories={categories}
            dishes={filteredDishes}
            getDishesForCategory={getDishesForCategory}
            onEditDish={openEditDish}
            onDeleteDish={handleDeleteDish}
          />
        </div>
      ) : (
        <CategorySection
          categories={categories} dishes={filteredDishes} dailyItems={dailyItems} menu={menu}
          getDishesForCategory={getDishesForCategory} openDishPicker={openDishPicker}
          openEditDish={openEditDish} handleDeleteDish={handleDeleteDish}
          handleRemoveDailyItem={handleRemoveDailyItem}
          onCreateCategory={() => setCategoryDialogOpen(true)}
        />
      )}

      {/* Dish Picker Dialog for daily menus */}
      {pickerOpen && (
        <DishPickerDialog
          open={pickerOpen} onOpenChange={setPickerOpen}
          menuId={menuId} categoryId={pickerCategoryId} categoryName={pickerCategoryName}
          restaurantId={currentRestaurant?.restaurant_id}
          existingDishIds={dailyItems.map(i => i.dish_id)}
          onLinked={reloadDailyItems}
        />
      )}

      {/* Beverage Selector */}
      <BeverageSelector
        open={beverageSelectorOpen}
        onClose={() => setBeverageSelectorOpen(false)}
        menuId={menuId}
        restaurantId={currentRestaurant?.restaurant_id}
        onAdded={fetchData}
      />
    </div>
  );
}
